import Controller, { inject as controller } from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, get, set, defineProperty } from '@ember/object';
import { inject as service } from '@ember/service';
import { optOutDefaultMessage } from 'garaje/models/abstract/abstract-gdpr-configuration';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { reject } from 'rsvp';
import { next } from '@ember/runloop';
import { restartableTask, dropTask } from 'ember-concurrency';
import Changeset from 'ember-changeset';
import ColorThief from 'color-thief';
import { alias, or } from 'macro-decorators';

import LOCALE_OPTIONS, { disabledLocalesInfo, localeUpdateWarning } from 'garaje/utils/locale-options';
import LocationValidations from 'garaje/validations/location';
import lookupValidator from 'ember-changeset-validations';
import manualPropagableHandler from 'garaje/utils/manual-propagable-handler';

const CONFIRM_DISABLE_LOCATION_MESSAGE =
  'Are you sure you want to deactivate this location? All iPads at this location will be logged out.';

const componentToHex = function (c) {
  const hex = c.toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
};

const rgbToHex = function (r, g, b) {
  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
};

export default class SettingsLocationController extends Controller {
  @service addressLookup;
  @service asyncExportManager;
  @service currentAdmin;
  @service flashMessages;
  @service('global-setting-batch') globalSettingBatchService;
  @service messageBus;
  @service router;
  @service store;
  @service skinnyLocations;
  @service visitorsOnboarding;
  @service featureFlags;

  @controller('billing.plans') plans;

  inputAddress = null;

  @tracked apiKeyCallback = null;
  @tracked changeset;

  @alias('model.vrSubscription') currentSubscription;
  @alias('globalSettingBatchService.currentGlobalSettingBatch') globalSettingBatch;
  @or('currentAdmin.isGlobalAdmin', 'currentAdmin.isLocationAdmin') isAdmin;

  constructor() {
    super(...arguments);
    defineProperty(this, 'localeUpdateWarning', localeUpdateWarning());
    defineProperty(this, 'disabledLocalesInfo', disabledLocalesInfo('model.ipads'));
  }

  get locationEnabled() {
    return !this.model.currentLocation.disabled;
  }

  get isDirty() {
    const { currentLocation } = this.model;
    const gdprConfig = get(currentLocation, 'gdprConfiguration');
    // if there is an error, the form is dirty because user
    // is unaware of the change they made is not saving
    const errors = get(this.changeset, 'errors');

    if (
      errors.length > 0 ||
      get(currentLocation, 'company.hasDirtyAttributes') ||
      get(gdprConfig, 'hasDirtyAttributes')
    ) {
      return true;
    }

    return (
      (get(this.changeset, 'isDirty') || currentLocation.hasDirtyAttributes) &&
      !get(currentLocation, 'isOnlyEmployeesCSVUploadStatusDirty')
    );
  }

  get isColorChanged() {
    return 'color' in this.model.currentLocation.changedAttributes();
  }

  _buildChangeset(model) {
    const validator = lookupValidator(LocationValidations);
    this.changeset = new Changeset(model, validator, LocationValidations);
  }

  get localeOptions() {
    if (!this.disabledLocalesInfo) {
      return LOCALE_OPTIONS;
    }
    const { disabledLocales } = this.disabledLocalesInfo;
    const disabledLocaleCodes = new Set(disabledLocales.map((locale) => locale.localeCode));
    return LOCALE_OPTIONS.map(({ value, label }) => {
      const disabled = disabledLocaleCodes.has(value);
      return { value, label, disabled };
    });
  }

  get showSetupGuide() {
    return this.isAdmin && this.featureFlags.isEnabled('growth_show_visitors_setup_guide_stepper');
  }

  @dropTask
  *deleteLogoTask() {
    set(this.model.currentLocation, 'logo', null);

    try {
      yield this.model.currentLocation.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
      if (this.visitorsOnboarding.showVideoWalkthrough) {
        this.visitorsOnboarding.loadLocationsTask.perform(true);
      }
    } catch (e) {
      this.model.currentLocation.rollbackAttributes();
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  }

  @dropTask
  *disableTask() {
    if (!window.confirm(CONFIRM_DISABLE_LOCATION_MESSAGE)) {
      return;
    }

    set(this.model.currentLocation, 'disabled', true);

    try {
      yield this.model.currentLocation.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      this.model.currentLocation.rollbackAttributes();
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  }

  @dropTask
  *saveLocationTask() {
    try {
      yield this.validateAndUpdateAddressTask.perform();
      yield this.changeset.save();

      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', e);
    }
  }

  @action
  setDataPrivacySetting(value) {
    const { currentLocation } = this.model;
    let gdprConfig = get(currentLocation, 'gdprConfiguration.content');

    if (!gdprConfig) {
      // create a record if there are none
      gdprConfig = this.store.createRecord('gdpr-configuration', {
        optOutVisitorProtocolEnabled: true,
        optOutVisitorProtocol: optOutDefaultMessage,
        location: currentLocation,
      });
      set(currentLocation, 'gdprConfiguration.content', gdprConfig);
    }
    set(gdprConfig, 'enabled', value);

    return gdprConfig;
  }

  @dropTask
  *setDataPrivacySettingTask() {
    try {
      yield this.model.currentLocation.gdprConfiguration.content.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      const errorText = parseErrorForDisplay(e);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  }

  @dropTask
  *setColorTask() {
    if (!this.isColorChanged) {
      return;
    }

    try {
      yield this.model.currentLocation.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
      if (this.visitorsOnboarding.showVideoWalkthrough) {
        this.visitorsOnboarding.loadLocationsTask.perform(true);
      }
    } catch (e) {
      this.model.currentLocation.rollbackAttributes();
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  }

  @restartableTask
  *validateAndUpdateAddressTask() {
    try {
      yield this.addressLookup.updateAddress(get(this.changeset, 'address'), this.changeset);
    } catch (error) {
      this.changeset.addError('address', error);
      yield reject(error);
    }
  }

  async detectImageColor(imagePromise) {
    const image = await imagePromise;
    const colorThief = new ColorThief();
    const dominantColor = colorThief.getColor(image);
    const palette = colorThief.getPalette(image, 5);
    const dominantHex = rgbToHex(dominantColor[0], dominantColor[1], dominantColor[2]);
    const actualColors = [];

    palette.forEach(function (color) {
      const difference1 = Math.abs(color[0] - color[1]);
      const difference2 = Math.abs(color[1] - color[2]);
      const difference3 = Math.abs(color[0] - color[2]);
      const differenceSum = difference1 + difference2 + difference3;

      if (differenceSum > 50) {
        const hexConvert = rgbToHex(color[0], color[1], color[2]);
        actualColors.push(hexConvert);
      }
    });

    if (actualColors.length > 0) {
      set(this.model, 'currentLocation.color', actualColors[0]);
    } else {
      set(this.model, 'currentLocation.color', dominantHex);
    }
    get(this.model, 'currentLocation').save();
  }

  @action
  expandSlideshow() {
    this.transitionToRoute('visitors.settings.welcome-screen.design').then(() => {
      next(this, function () {
        this.messageBus.trigger('expandSlideshow');
      });
    });
  }

  @action
  expandWelcomeImage() {
    this.transitionToRoute('visitors.settings.welcome-screen.design').then(() => {
      // allow messageBus to install first on the component
      // before triggering
      next(this, function () {
        this.messageBus.trigger('expandWelcomeImage');
      });
    });
  }

  @action
  exportEntries() {
    const params = { location: this.model.currentLocation, sort: '-signed-in-at' };

    return this.asyncExportManager.exportEntriesTask.perform(params);
  }

  @action
  setLogo({ data }, promise) {
    this.store.push({
      data: {
        type: 'location',
        id: data.id,
        attributes: {
          logo: data.attributes.logo,
        },
      },
    });
    this.detectImageColor(promise);
    manualPropagableHandler(this.globalSettingBatch, 'location', 'logo');
    if (this.visitorsOnboarding.showVideoWalkthrough) {
      this.visitorsOnboarding.loadLocationsTask.perform(true);
    }
  }

  @action
  regenerateApiKey() {
    if (
      window.confirm(
        'Are you sure you want to regenerate the API key? Existing API clients will unable to access Envoy.'
      )
    ) {
      this.apiKeyCallback = this.model.currentLocation.generateApiKey();
    }
  }

  @action
  selectPlan(callback, plan, period) {
    const params = { queryParams: { plan, period } };
    callback(this.transitionToRoute('billing.subscribe', params));
  }

  @action
  rollback() {
    const currentLocation = get(this.model, 'currentLocation');
    const config = get(currentLocation, 'gdprConfiguration.content');
    currentLocation.rollbackAttributes();
    get(currentLocation, 'company.content').rollbackAttributes();
    this.changeset.rollback();
    if (config) {
      config.rollbackAttributes();
    }
  }

  @action
  transitionTo(route = '') {
    this.router.transitionTo(route);
  }
}
